import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import type {
  TypedMutationTrigger,
  TypedUseMutationResult,
} from "@reduxjs/toolkit/query/react";

import type {
  ApproveMarkdownArgs,
  ApproveMarkdownResponseDto,
  RefuseMarkdownArgs,
  RefuseMarkdownResponseDto,
  SubmitMarkdownArgs,
  SubmitMarkdownResponseDto,
} from "@/@types/price-markdown-lifecycle";
import { Tags } from "@/enum/tags";
import { apiSlice } from "@/services/http";

type MarkdownTransitionMutation<TResult, TArgs> = readonly [
  TypedMutationTrigger<TResult, TArgs, BaseQueryFn>,
  TypedUseMutationResult<TResult, TArgs, BaseQueryFn>,
];

export type SubmitMarkdownMutation = MarkdownTransitionMutation<
  SubmitMarkdownResponseDto,
  SubmitMarkdownArgs
>;

export type ApproveMarkdownMutation = MarkdownTransitionMutation<
  ApproveMarkdownResponseDto,
  ApproveMarkdownArgs
>;

export type RefuseMarkdownMutation = MarkdownTransitionMutation<
  RefuseMarkdownResponseDto,
  RefuseMarkdownArgs
>;

export const priceMarkdownStateApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    submitMarkdown: builder.mutation<
      SubmitMarkdownResponseDto,
      SubmitMarkdownArgs
    >({
      query: ({ markdownId }) => ({
        url: `/price-markdown/selection/drill/${markdownId}/submit`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { markdownId }) => [
        { type: Tags.PRICE_MARKDOWN_DRILL, id: markdownId },
        { type: Tags.PRICE_MARKDOWN_VERBA_SUMMARY, id: markdownId },
        Tags.PRICE_MARKDOWN_SELECAO,
        Tags.PRICE_MARKDOWN_DRILL,
      ],
    }),

    approveMarkdown: builder.mutation<
      ApproveMarkdownResponseDto,
      ApproveMarkdownArgs
    >({
      query: ({ markdownId }) => ({
        url: `/price-markdown/selection/drill/${markdownId}/approve`,
        method: "POST",
        body: { markdownId },
      }),
      invalidatesTags: (_result, _error, { markdownId }) => [
        { type: Tags.PRICE_MARKDOWN_DRILL, id: markdownId },
        { type: Tags.PRICE_MARKDOWN_VERBA_SUMMARY, id: markdownId },
        Tags.PRICE_MARKDOWN_SELECAO,
        Tags.PRICE_MARKDOWN_CARTA,
        Tags.PRICE_MARKDOWN_DRILL,
      ],
    }),

    refuseMarkdown: builder.mutation<
      RefuseMarkdownResponseDto,
      RefuseMarkdownArgs
    >({
      query: ({ markdownId, comment }) => ({
        url: `/price-markdown/selection/drill/${markdownId}/refuse`,
        method: "POST",
        body: { markdownId, comment },
      }),
      invalidatesTags: (_result, _error, { markdownId }) => [
        { type: Tags.PRICE_MARKDOWN_DRILL, id: markdownId },
        { type: Tags.PRICE_MARKDOWN_VERBA_SUMMARY, id: markdownId },
        Tags.PRICE_MARKDOWN_SELECAO,
        Tags.PRICE_MARKDOWN_CARTA,
        Tags.PRICE_MARKDOWN_DRILL,
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useSubmitMarkdownMutation,
  useApproveMarkdownMutation,
  useRefuseMarkdownMutation,
} = priceMarkdownStateApi;
